import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useNoIndex } from "@/lib/useNoIndex";
import { emailPrivacyNotice } from "@/lib/privacy";
import { t } from "@/lib/i18n";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function ThankYouPage() {
  useNoIndex();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang") === "de" ? "de" : "fi";
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t("thankyou.err.emailEmpty", {}, lang));
      return;
    }
    setSaving(true);
    try {
      await api.post(`/collections/${slug}/leads`, { email: email.trim() });
      setSaved(true);
    } catch (err) {
      toast.error(t("thankyou.err.saveFailed", {}, lang));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FDFBF7] px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full"
      >
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-[#F8EBE6] grid place-items-center mx-auto mb-5">
            <Heart className="w-7 h-7 text-[#C86D51]" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium text-[#2C221E]" data-testid="thankyou-heading">
            {t("thankyou.heading", {}, lang)}
          </h1>
          <p className="mt-3 text-lg text-[#7A6B63]">{t("thankyou.saved", {}, lang)}</p>
        </div>

        <div className="mt-8 rounded-3xl border border-[#E8DEC2] bg-white p-6 sm:p-8 shadow-sm text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#2C221E] leading-snug">
            {t("thankyou.ctaTitle", {}, lang)}
          </h2>

          <a
            href="/"
            className="mt-6 inline-flex items-center justify-center w-full h-14 rounded-xl bg-[#C86D51] hover:bg-[#B35A3E] text-white text-lg font-medium transition-colors"
            data-testid="make-own-portrait-button"
          >
            {t("thankyou.ctaButton", {}, lang)}
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>

          <div className="mt-8 pt-6 border-t border-[#F0E8DA]">
            {saved ? (
              <div className="flex items-center gap-3 rounded-2xl bg-[#EAF1EA] border border-[#CBDDCB] p-4 text-[#3f6047] text-left" data-testid="lead-confirmation">
                <Check className="w-5 h-5 shrink-0" />
                <span className="text-sm">{t("thankyou.leadConfirmation", {}, lang)}</span>
              </div>
            ) : (
              <form onSubmit={submit} className="text-left" data-testid="lead-form">
                <label htmlFor="lead-email" className="block text-sm text-[#7A6B63] leading-relaxed mb-3">
                  {t("thankyou.leadLabel", {}, lang)}
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    id="lead-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("common.emailPlaceholder", {}, lang)}
                    className="h-12 flex-1 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                    data-testid="lead-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={saving}
                    variant="outline"
                    className="h-12 px-6 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"
                    data-testid="lead-submit-button"
                  >
                    {saving ? t("common.sending", {}, lang) : t("common.send", {}, lang)}
                  </Button>
                </div>
                <div className="mt-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button type="button" className="text-sm text-[#7A6B63] hover:text-[#B35A3E] hover:underline" data-testid="email-privacy-link">
                        {t("thankyou.privacy", {}, lang)}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                      <DialogHeader>
                        <DialogTitle className="font-serif">{t("thankyou.privacy", {}, lang)}</DialogTitle>
                      </DialogHeader>
                      <p className="text-sm text-[#4A3E39] leading-relaxed" data-testid="email-privacy-text">
                        {emailPrivacyNotice(lang)}
                      </p>
                    </DialogContent>
                  </Dialog>
                </div>
              </form>
            )}
          </div>
        </div>
      </motion.div>
      <Footer slug={slug} />
    </div>
  );
}
